import { router } from '@inertiajs/react';

declare global {
    interface Window {
        gtag?: (
            command: 'config' | 'event' | 'js',
            targetId: string | Date,
            config?: Record<string, unknown>,
        ) => void;
        dataLayer: unknown[];
    }
}

class Analytics {
    private measurementId: string | null = null;
    private isInitialized = false;
    private navigationListener: (() => void) | null = null;
    private hasTrackedInitialPageView = false;

    constructor() {
        this.measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID || null;
    }

    private async loadGtagScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.measurementId || this.isLoaded()) {
                resolve();
                return;
            }

            // Create gtag data layer FIRST
            window.dataLayer = window.dataLayer || [];

            // Define gtag function
            window.gtag = function gtag() {
                // eslint-disable-next-line prefer-rest-params
                window.dataLayer.push(arguments);
            };

            // Initialize gtag timestamp
            window.gtag('js', new Date());

            // Load gtag script BEFORE config
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;

            script.onload = () => {
                // Configure gtag AFTER script loads
                if (window.gtag && this.measurementId) {
                    window.gtag('config', this.measurementId, {
                        send_page_view: false,
                        anonymize_ip: true,
                    });
                }
                resolve();
            };

            script.onerror = () =>
                reject(new Error('Failed to load Google Analytics script'));

            document.head.appendChild(script);
        });
    }

    private isLoaded(): boolean {
        return typeof window.gtag === 'function';
    }

    private trackPageView(path: string): void {
        if (!this.isInitialized || !window.gtag || !this.measurementId) {
            return;
        }

        // Use full URL instead of just pathname
        const fullUrl = window.location.origin + path;

        window.gtag('event', 'page_view', {
            page_location: fullUrl,
            page_path: path,
            page_title: document.title,
        });
    }

    public async initialize(): Promise<void> {
        if (!this.measurementId || this.isInitialized) {
            return;
        }

        try {
            await this.loadGtagScript();
            this.isInitialized = true;

            // Add small delay to ensure gtag is fully ready
            setTimeout(() => {
                // Track initial page view
                this.trackPageView(
                    window.location.pathname + window.location.search,
                );
                this.hasTrackedInitialPageView = true;
            }, 200);

            // Set up Inertia navigation tracking
            this.navigationListener = router.on('navigate', (event) => {
                if (!this.hasTrackedInitialPageView) {
                    // Skipping first navigate event (initial load)
                    this.hasTrackedInitialPageView = true;
                    return;
                }
                // Small delay to ensure DOM is updated
                setTimeout(() => {
                    this.trackPageView(event.detail.page.url);
                }, 50);
            });
        } catch (error) {
            console.warn('Failed to initialize Google Analytics:', error);
        }
    }

    public track(
        eventName: string,
        parameters: Record<string, unknown> = {},
    ): void {
        if (!this.isInitialized || !window.gtag || !this.measurementId) {
            return;
        }

        window.gtag('event', eventName, parameters);
    }

    public cleanup(): void {
        if (this.navigationListener) {
            this.navigationListener();
            this.navigationListener = null;
        }
    }
}

// Create and export singleton instance
const analytics = new Analytics();
export default analytics;
